import { useEffect } from 'react';

import { useGetVouchers } from './useVoucherQuery';

export const OPTIMISTIC_ACTIVE_VOUCHER_COUNT_KEY = 'hub.optimisticActiveVoucherCount';

export const setOptimisticActiveVoucherCount = (count: number) => {
  sessionStorage.setItem(OPTIMISTIC_ACTIVE_VOUCHER_COUNT_KEY, String(count));
};

export const clearOptimisticActiveVoucherCount = () => {
  sessionStorage.removeItem(OPTIMISTIC_ACTIVE_VOUCHER_COUNT_KEY);
};

const getOptimisticActiveVoucherCount = (): number | null => {
  const value = sessionStorage.getItem(OPTIMISTIC_ACTIVE_VOUCHER_COUNT_KEY);
  if (value === null) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Active voucher count shared by VoucherMessage, VoucherWidget, etc.
 * Supports an optional override (e.g. from router state) and a sessionStorage
 * optimistic count set when a voucher is used before the query refetches.
 * @param {number | undefined} override - Optional override for the voucher count (e.g. from router state). Defaults to undefined.
 * @returns {object} The active voucher count, the vouchers, the loading state, the error, and the error state.
 */
export const useActiveVoucherCount = (override?: number) => {
  const { data: vouchers, isLoading, error, isError } = useGetVouchers('Active');
  const optimisticCount = getOptimisticActiveVoucherCount();

  useEffect(() => {
    if (optimisticCount !== null && vouchers !== undefined && vouchers.length <= optimisticCount) {
      clearOptimisticActiveVoucherCount();
    }
  }, [vouchers, optimisticCount]);

  const voucherCount =
    override !== undefined ? override : optimisticCount !== null ? optimisticCount : (vouchers?.length ?? 0);

  return {
    voucherCount,
    vouchers,
    isLoading,
    error,
    isError,
  };
};
